import { HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ENDPOINTS } from 'app/core/constants/endpoints';
import { ROUTES } from 'app/core/constants/routes';
import { SKIP_AUTH_REFRESH } from 'app/core/interceptors/http-context-tokens';
import { ApiService } from 'app/core/services/api.service';
import { DataStorageService } from 'app/shared/utils/data-storage-service';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { LoginResponse } from '../models/login-response.model';
import { OperatorUser } from '../models/operator-user.model';

@Injectable({
  providedIn: 'root',
})
export class OperatorAuthService {
  private currentUserSubject = new BehaviorSubject<OperatorUser | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();
  /** Non-secret marker; the JWT is an httpOnly cookie (X-Client-App: operator). */
  private signedInKey = 'operator_signed_in';

  constructor(
    private apiService: ApiService,
    private router: Router,
    private dataStorageService: DataStorageService
  ) {
  }

  login(email: string, password: string): Observable<LoginResponse> {
    return this.apiService
      .post<LoginResponse>(
        ENDPOINTS.OPERATOR_AUTH.LOGIN,
        { email, password },
        { context: new HttpContext().set(SKIP_AUTH_REFRESH, true) }
      )
      .pipe(
        tap((response: LoginResponse) => {
          if (response.status.success) {
            this.dataStorageService.set(this.signedInKey, '1');
            this.currentUserSubject.next(response.data.user);
          }
        })
      );
  }

  completeRegistration(
    token: string,
    password: string,
    confirmPassword: string
  ): Observable<LoginResponse> {
    return this.apiService.post<LoginResponse>(
      ENDPOINTS.OPERATOR_AUTH.COMPLETE_REGISTRATION(token),
      {
        password,
        password_confirmation: confirmPassword,
      },
      { context: new HttpContext().set(SKIP_AUTH_REFRESH, true) }
    );
  }

  refreshToken(): Observable<any> {
    return this.apiService.post(ENDPOINTS.OPERATOR_AUTH.REFRESH, {}, {
      context: new HttpContext().set(SKIP_AUTH_REFRESH, true),
    });
  }

  /** Session ended server-side (refresh rejected). */
  clearSession(): void {
    this.dataStorageService.remove(this.signedInKey);
    this.currentUserSubject.next(null);
  }

  logout(): Observable<any> {
    return this.apiService.post(ENDPOINTS.OPERATOR_AUTH.LOGOUT, {}).pipe(
      tap(() => {
        this.dataStorageService.remove(this.signedInKey);
        this.currentUserSubject.next(null);
        this.router.navigate([ROUTES.OPERATOR.LOGIN]);
      })
    );
  }

  isLoggedIn(): boolean {
    return this.dataStorageService.get(this.signedInKey) === '1';
  }

  getCurrentUser(): OperatorUser | null {
    return this.currentUserSubject.value;
  }
}
